import { Alert, AutoComplete, Button, Card, Form, Input, InputNumber, Popconfirm, Space, Switch, Table, Tag, Typography, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MarkdownContent from '../components/MarkdownContent';
import {
  createAdminHelpArticle,
  createAdminHelpImagePresign,
  deleteAdminHelpArticle,
  listAdminHelpArticles,
  updateAdminHelpArticle,
} from '../services/api';
import type { CurrentUser, HelpArticle, HelpArticleCreate } from '../types/incident';

const { Link, Paragraph, Text } = Typography;

type AdminHelpPageProps = {
  currentUser: CurrentUser | null;
};

type HelpArticleFormValues = {
  slug: string;
  category: string;
  title: string;
  summary?: string;
  body_markdown: string;
  keywords?: string;
  is_published: boolean;
  sort_order: number;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const parseKeywords = (value?: string): string[] =>
  (value || '')
    .split(',')
    .map((keyword) => keyword.trim())
    .filter(Boolean);

const formatKeywords = (keywords: string[]): string => keywords.join(', ');

const HELP_IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const HELP_IMAGE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

const imageAltTextFromFileName = (fileName: string): string => {
  const name = fileName.replace(/\.[^.]+$/, '').trim();
  return name || 'image';
};

const buildPayload = (values: HelpArticleFormValues): HelpArticleCreate => ({
  slug: values.slug.trim(),
  category: values.category.trim(),
  title: values.title.trim(),
  summary: values.summary?.trim() || null,
  body_markdown: values.body_markdown.trim(),
  keywords: parseKeywords(values.keywords),
  is_published: values.is_published,
  sort_order: values.sort_order ?? 100,
});

const AdminHelpPage = ({ currentUser }: AdminHelpPageProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<HelpArticleFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const bodyTextAreaRef = useRef<TextAreaRef>(null);
  const bodyMarkdown = Form.useWatch('body_markdown', form) || '';

  const isSystemAdmin = useMemo(
    () => currentUser?.assignments.some((assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active) || false,
    [currentUser?.assignments]
  );

  const categoryOptions = useMemo(
    () =>
      Array.from(new Set(articles.map((article) => article.category).filter(Boolean)))
        .sort((left, right) => left.localeCompare(right))
        .map((category) => ({ value: category })),
    [articles]
  );

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      setArticles(await listAdminHelpArticles());
    } catch {
      messageApi.error(t('adminHelp.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadArticles();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, loadArticles]);

  const resetForm = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ category: t('adminHelp.defaults.category'), is_published: false, sort_order: 100 });
  };

  const handleEdit = (article: HelpArticle) => {
    setEditingId(article.id);
    form.setFieldsValue({
      slug: article.slug,
      category: article.category,
      title: article.title,
      summary: article.summary || undefined,
      body_markdown: article.body_markdown,
      keywords: formatKeywords(article.keywords),
      is_published: article.is_published,
      sort_order: article.sort_order,
    });
  };

  const handleSubmit = async (values: HelpArticleFormValues) => {
    setSaving(true);
    try {
      const payload = buildPayload(values);
      if (editingId) {
        const updated = await updateAdminHelpArticle(editingId, payload);
        setArticles((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        messageApi.success(t('adminHelp.messages.updated'));
      } else {
        const created = await createAdminHelpArticle(payload);
        setArticles((current) => [...current, created]);
        messageApi.success(t('adminHelp.messages.created'));
      }
      resetForm();
      await loadArticles();
    } catch {
      messageApi.error(t('adminHelp.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (articleId: string) => {
    try {
      await deleteAdminHelpArticle(articleId);
      setArticles((current) => current.filter((item) => item.id !== articleId));
      messageApi.success(t('adminHelp.messages.deleted'));
      if (editingId === articleId) resetForm();
    } catch {
      messageApi.error(t('adminHelp.messages.deleteFailed'));
    }
  };

  const appendImageMarkdown = useCallback(
    (publicPath: string, fileName: string) => {
      const currentBody = form.getFieldValue('body_markdown') || '';
      const textArea = bodyTextAreaRef.current?.resizableTextArea?.textArea;
      const selectionStart = textArea?.selectionStart ?? currentBody.length;
      const selectionEnd = textArea?.selectionEnd ?? selectionStart;
      const before = currentBody.slice(0, selectionStart);
      const after = currentBody.slice(selectionEnd);
      const imageMarkdown = `![${imageAltTextFromFileName(fileName)}](${publicPath})`;
      const prefix = before.length === 0 || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
      const suffix = after.length === 0 || after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n';
      const inserted = `${prefix}${imageMarkdown}${suffix}`;
      const nextBody = `${before}${inserted}${after}`;
      const nextCursorPosition = before.length + prefix.length + imageMarkdown.length;
      form.setFieldsValue({
        body_markdown: nextBody,
      });
      requestAnimationFrame(() => {
        const nextTextArea = bodyTextAreaRef.current?.resizableTextArea?.textArea;
        nextTextArea?.focus();
        nextTextArea?.setSelectionRange(nextCursorPosition, nextCursorPosition);
      });
    },
    [form]
  );

  const imageUploadProps: UploadProps = useMemo(
    () => ({
      accept: HELP_IMAGE_CONTENT_TYPES.join(','),
      disabled: imageUploading,
      showUploadList: false,
      customRequest: async ({ file, onError, onSuccess }) => {
        const uploadFile = file as File;
        if (!HELP_IMAGE_CONTENT_TYPES.includes(uploadFile.type)) {
          messageApi.error(t('adminHelp.messages.imageUnsupported'));
          onError?.(new Error('unsupported image content type'));
          return;
        }
        if (uploadFile.size > HELP_IMAGE_MAX_SIZE_BYTES) {
          messageApi.error(t('adminHelp.messages.imageTooLarge'));
          onError?.(new Error('image is too large'));
          return;
        }

        setImageUploading(true);
        try {
          const presign = await createAdminHelpImagePresign({
            file_name: uploadFile.name,
            content_type: uploadFile.type,
            size_bytes: uploadFile.size,
          });
          const uploadResponse = await fetch(presign.upload_url, {
            method: 'PUT',
            headers: presign.headers,
            body: uploadFile,
          });
          if (!uploadResponse.ok) {
            throw new Error(`image upload failed: ${uploadResponse.status}`);
          }
          appendImageMarkdown(presign.public_path, uploadFile.name);
          messageApi.success(t('adminHelp.messages.imageUploaded'));
          onSuccess?.(presign);
        } catch (error) {
          messageApi.error(t('adminHelp.messages.imageUploadFailed'));
          onError?.(error as Error);
        } finally {
          setImageUploading(false);
        }
      },
    }),
    [appendImageMarkdown, imageUploading, messageApi, t]
  );

  const columns: ColumnsType<HelpArticle> = [
    {
      title: t('adminHelp.table.article'),
      dataIndex: 'title',
      key: 'title',
      render: (_value, article) => (
        <Space direction="vertical" size={2}>
          <Text strong>{article.title}</Text>
          <Text type="secondary">{article.slug}</Text>
          {article.summary && (
            <Paragraph className="adminHelpSummaryPreview" type="secondary">
              {article.summary}
            </Paragraph>
          )}
        </Space>
      ),
    },
    {
      title: t('adminHelp.table.category'),
      dataIndex: 'category',
      key: 'category',
      width: 160,
    },
    {
      title: t('adminHelp.table.status'),
      dataIndex: 'is_published',
      key: 'is_published',
      width: 120,
      render: (isPublished: boolean) => (
        <Tag color={isPublished ? 'green' : 'default'}>
          {isPublished ? t('adminHelp.status.published') : t('adminHelp.status.draft')}
        </Tag>
      ),
    },
    {
      title: t('adminHelp.table.updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: formatDateTime,
    },
    {
      title: t('adminHelp.table.actions'),
      key: 'actions',
      width: 210,
      render: (_value, article) => (
        <Space>
          {article.is_published && (
            <Button size="small" href={`/help/${article.slug}`} target="_blank" rel="noreferrer">
              {t('adminHelp.actions.view')}
            </Button>
          )}
          <Button size="small" onClick={() => handleEdit(article)}>
            {t('adminHelp.actions.edit')}
          </Button>
          <Popconfirm
            title={t('adminHelp.actions.deleteConfirmTitle')}
            description={t('adminHelp.actions.deleteConfirmDescription')}
            okText={t('common.yes')}
            cancelText={t('common.cancel')}
            onConfirm={() => handleDelete(article.id)}
          >
            <Button danger size="small">
              {t('adminHelp.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isSystemAdmin) {
    return (
      <div className="page">
        {contextHolder}
        <Alert type="warning" showIcon message={t('adminHelp.forbidden')} />
      </div>
    );
  }

  return (
    <div className="page adminHelpPage">
      {contextHolder}
      <div className="pageHeader">
        <p>{t('adminHelp.description')}</p>
      </div>

      <div className="adminHelpEditorLayout">
        <Card
          className="adminHelpFormCard"
          title={editingId ? t('adminHelp.form.editTitle') : t('adminHelp.form.createTitle')}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ category: t('adminHelp.defaults.category'), is_published: false, sort_order: 100 }}
            onFinish={handleSubmit}
          >
            <Space className="adminHelpFieldRow" size={16} wrap>
              <Form.Item
                name="title"
                label={t('adminHelp.form.title')}
                rules={[{ required: true, message: t('validation.required', { label: t('adminHelp.form.title') }) }]}
              >
                <Input maxLength={200} />
              </Form.Item>
              <Form.Item
                name="slug"
                label={t('adminHelp.form.slug')}
                rules={[
                  { required: true, message: t('validation.required', { label: t('adminHelp.form.slug') }) },
                  { pattern: /^[a-z0-9][a-z0-9-]*$/, message: t('adminHelp.validation.slug') },
                ]}
              >
                <Input maxLength={120} />
              </Form.Item>
            </Space>
            <Space className="adminHelpFieldRow" size={16} wrap>
              <Form.Item
                name="category"
                label={t('adminHelp.form.category')}
                rules={[{ required: true, message: t('validation.required', { label: t('adminHelp.form.category') }) }]}
              >
                <AutoComplete
                  filterOption={(inputValue, option) =>
                    (option?.value || '').toLowerCase().includes(inputValue.toLowerCase())
                  }
                  maxLength={120}
                  options={categoryOptions}
                />
              </Form.Item>
              <Form.Item name="sort_order" label={t('adminHelp.form.sortOrder')}>
                <InputNumber min={0} max={100000} />
              </Form.Item>
            </Space>
            <Form.Item name="summary" label={t('adminHelp.form.summary')}>
              <Input.TextArea rows={2} maxLength={1000} showCount />
            </Form.Item>
            <Form.Item name="keywords" label={t('adminHelp.form.keywords')}>
              <Input placeholder={t('adminHelp.form.keywordsPlaceholder')} />
            </Form.Item>
            <Form.Item
              name="body_markdown"
              label={
                <Space size={10}>
                  <span>{t('adminHelp.form.bodyMarkdown')}</span>
                  <Link href="/help/markdown-guide" target="_blank" rel="noreferrer">
                    {t('adminHelp.markdownGuide.articleLink')}
                  </Link>
                  <Upload {...imageUploadProps}>
                    <Button size="small" loading={imageUploading}>
                      {t('adminHelp.actions.uploadImage')}
                    </Button>
                  </Upload>
                </Space>
              }
              rules={[{ required: true, message: t('validation.required', { label: t('adminHelp.form.bodyMarkdown') }) }]}
            >
              <Input.TextArea ref={bodyTextAreaRef} rows={14} maxLength={50000} showCount />
            </Form.Item>
            <Space className="adminHelpActionRow">
              <Form.Item name="is_published" label={t('adminHelp.form.published')} valuePropName="checked">
                <Switch />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingId ? t('adminHelp.actions.update') : t('adminHelp.actions.create')}
              </Button>
              {editingId && <Button onClick={resetForm}>{t('common.cancel')}</Button>}
            </Space>
          </Form>
        </Card>

        <Card className="adminHelpPreviewCard" title={t('adminHelp.previewTitle')}>
          {bodyMarkdown ? <MarkdownContent content={bodyMarkdown} /> : <Text type="secondary">{t('adminHelp.previewEmpty')}</Text>}
        </Card>
      </div>

      <Card className="adminHelpTableCard" title={t('adminHelp.listTitle')}>
        <Table rowKey="id" columns={columns} dataSource={articles} loading={loading} pagination={{ pageSize: 20 }} />
      </Card>
    </div>
  );
};

export default AdminHelpPage;
